import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Swal from "sweetalert2";
import { Plus, Search, TicketPercent, Trash2, X } from "lucide-react";
import { CreateCoupon, DeleteCoupon, GetCoupons, UpdateCouponStatus } from "../../common/CommonApi";

const EMPTY_FORM = { couponCode: "", couponName: "", amount: "", status: "active" };

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatingCode, setUpdatingCode] = useState("");

  const loadCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const response = await GetCoupons();
      if (response.status === 200) {
        setCoupons(response.data.data || []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  const filteredCoupons = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return coupons;
    return coupons.filter(
      (coupon) =>
        coupon.couponCode.toLowerCase().includes(search) ||
        coupon.couponName.toLowerCase().includes(search)
    );
  }, [coupons, query]);

  const closeModal = () => {
    if (saving) return;
    setIsOpen(false);
    setForm(EMPTY_FORM);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: name === "couponCode" ? value.toUpperCase() : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const couponCode = form.couponCode.trim();
    const couponName = form.couponName.trim();
    const amount = Number(form.amount);

    if (!couponCode || !couponName || !Number.isFinite(amount) || amount <= 0) {
      Swal.fire("Invalid coupon", "Enter a coupon code, name, and amount greater than 0.", "warning");
      return;
    }

    setSaving(true);
    try {
      const response = await CreateCoupon({
        couponCode,
        couponName,
        amount,
        status: form.status === "active",
      });
      if (response.status === 201) {
        setCoupons((current) => [response.data.data, ...current]);
        setIsOpen(false);
        setForm(EMPTY_FORM);
        Swal.fire("Coupon added", "The coupon was saved successfully.", "success");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (couponCode) => {
    const result = await Swal.fire({
      title: "Delete coupon?",
      text: `${couponCode} will be permanently removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Delete",
    });

    if (!result.isConfirmed) return;

    const response = await DeleteCoupon(couponCode);
    if (response.status === 200) {
      setCoupons((current) => current.filter((coupon) => coupon.couponCode !== couponCode));
      Swal.fire("Deleted", "The coupon was removed.", "success");
    }
  };

  const handleStatusChange = async (coupon) => {
    const newStatus = !coupon.status;
    setUpdatingCode(coupon.couponCode);
    try {
      const response = await UpdateCouponStatus(coupon.couponCode, newStatus);
      if (response.status === 200) {
        setCoupons((current) =>
          current.map((item) =>
            item.couponCode === coupon.couponCode ? response.data.data : item
          )
        );
      }
    } finally {
      setUpdatingCode("");
    }
  };

  return (
    <Page>
      <PageHeader>
        <div>
          <Title>Coupons</Title>
          <Subtitle>Create and manage discount coupons.</Subtitle>
        </div>
        <PrimaryButton type="button" onClick={() => setIsOpen(true)}>
          <Plus size={18} /> Add Coupon
        </PrimaryButton>
      </PageHeader>

      <Stats>
        <StatIcon><TicketPercent size={22} /></StatIcon>
        <div>
          <StatValue>{coupons.length}</StatValue>
          <StatLabel>Total coupons</StatLabel>
        </div>
      </Stats>

      <Card>
        <Toolbar>
          <SearchBox>
            <Search size={18} />
            <SearchInput
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by coupon code or name"
            />
          </SearchBox>
        </Toolbar>

        <TableScroll>
          <Table>
            <thead>
              <tr>
                <Th>Coupon code</Th>
                <Th>Coupon name</Th>
                <Th>Amount</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th align="right">Action</Th>
              </tr>
            </thead>
            <tbody>
              {filteredCoupons.map((coupon) => (
                <tr key={coupon.couponCode}>
                  <Td><Code>{coupon.couponCode}</Code></Td>
                  <Td>{coupon.couponName}</Td>
                  <Td><Amount>₹{Number(coupon.amount).toLocaleString("en-IN")}</Amount></Td>
                  <Td>
                    <StatusButton
                      type="button"
                      $active={coupon.status}
                      disabled={updatingCode === coupon.couponCode}
                      onClick={() => handleStatusChange(coupon)}
                      title={`Mark ${coupon.couponCode} as ${coupon.status ? "inactive" : "active"}`}
                    >
                      <StatusDot />
                      {updatingCode === coupon.couponCode
                        ? "Updating..."
                        : coupon.status ? "Active" : "Inactive"}
                    </StatusButton>
                  </Td>
                  <Td>{new Date(coupon.createdAt).toLocaleString("en-IN")}</Td>
                  <Td align="right">
                    <DeleteButton
                      type="button"
                      title={`Delete ${coupon.couponCode}`}
                      onClick={() => handleDelete(coupon.couponCode)}
                    >
                      <Trash2 size={17} /> Delete
                    </DeleteButton>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>

        {!loading && filteredCoupons.length === 0 && (
          <EmptyState>
            <TicketPercent size={34} />
            <strong>{query ? "No matching coupons" : "No coupons added yet"}</strong>
            <span>{query ? "Try a different search." : "Add your first coupon to get started."}</span>
          </EmptyState>
        )}
        {loading && <Loading>Loading coupons...</Loading>}
      </Card>

      {isOpen && (
        <Overlay onMouseDown={(event) => event.target === event.currentTarget && closeModal()}>
          <Modal role="dialog" aria-modal="true" aria-labelledby="coupon-modal-title">
            <ModalHeader>
              <div>
                <ModalTitle id="coupon-modal-title">Add Coupon</ModalTitle>
                <ModalSubtitle>Enter the new coupon details below.</ModalSubtitle>
              </div>
              <IconButton type="button" onClick={closeModal} aria-label="Close">
                <X size={20} />
              </IconButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <Field>
                <Label htmlFor="couponCode">Coupon code</Label>
                <Input
                  id="couponCode"
                  name="couponCode"
                  value={form.couponCode}
                  onChange={handleChange}
                  placeholder="e.g. SAVE500"
                  maxLength={50}
                  autoFocus
                  required
                />
              </Field>
              <Field>
                <Label htmlFor="couponName">Coupon name</Label>
                <Input
                  id="couponName"
                  name="couponName"
                  value={form.couponName}
                  onChange={handleChange}
                  placeholder="e.g. Summer discount"
                  maxLength={100}
                  required
                />
              </Field>
              <Field>
                <Label htmlFor="amount">Coupon amount</Label>
                <AmountInput>
                  <span>₹</span>
                  <Input
                    id="amount"
                    name="amount"
                    type="number"
                    value={form.amount}
                    onChange={handleChange}
                    placeholder="500"
                    min="0.01"
                    step="0.01"
                    required
                  />
                </AmountInput>
              </Field>
              <Field>
                <Label htmlFor="status">Status</Label>
                <Select
                  id="status"
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Select>
              </Field>
              <Actions>
                <SecondaryButton type="button" onClick={closeModal}>Cancel</SecondaryButton>
                <PrimaryButton type="submit" disabled={saving}>
                  {saving ? "Saving..." : "Add Coupon"}
                </PrimaryButton>
              </Actions>
            </Form>
          </Modal>
        </Overlay>
      )}
    </Page>
  );
};

export default Coupons;

const Page = styled.div`max-width: 1400px; margin: 0 auto;`;
const PageHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-bottom: 22px;
  @media (max-width: 640px) {
    align-items: stretch;
    flex-direction: column;

    button { width: 100%; }
  }
`;
const Title = styled.h1`
  margin: 0; color: #172033; font-size: 28px;
  @media (max-width: 480px) { font-size: 24px; }
`;
const Subtitle = styled.p`margin: 6px 0 0; color: #64748b; font-size: 14px;`;
const PrimaryButton = styled.button`
  border: 0; border-radius: 8px; padding: 11px 17px; background: #30d5c8; color: #fff;
  display: inline-flex; align-items: center; justify-content: center; gap: 7px; cursor: pointer; font-weight: 600;
  &:hover { background: #26a79d; }
  &:disabled { cursor: not-allowed; opacity: .65; }
`;
const Stats = styled.div`
  width: fit-content; min-width: 210px; display: flex; align-items: center; gap: 12px; padding: 16px 18px;
  border: 1px solid #e2e8f0; border-radius: 12px; background: #fff; margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, .06);
  @media (max-width: 480px) { width: 100%; min-width: 0; }
`;
const StatIcon = styled.div`
  width: 42px; height: 42px; border-radius: 10px; display: grid; place-items: center;
  color: #168c84; background: #dcf9f6;
`;
const StatValue = styled.div`font-size: 22px; color: #172033; font-weight: 700; line-height: 1;`;
const StatLabel = styled.div`font-size: 13px; color: #64748b; margin-top: 5px;`;
const Card = styled.div`
  background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden;
  box-shadow: 0 1px 3px rgba(15, 23, 42, .06);
`;
const Toolbar = styled.div`display: flex; padding: 16px; border-bottom: 1px solid #eef2f7;`;
const SearchBox = styled.div`
  width: min(100%, 380px); display: flex; align-items: center; gap: 9px; padding: 9px 12px;
  border: 1px solid #dbe3ed; border-radius: 8px; color: #94a3b8; background: #f8fafc;
  &:focus-within { border-color: #30d5c8; box-shadow: 0 0 0 3px rgba(48, 213, 200, .12); }
`;
const SearchInput = styled.input`border: 0; outline: 0; background: transparent; width: 100%; font-size: 14px;`;
const TableScroll = styled.div`overflow-x: auto;`;
const Table = styled.table`width: 100%; min-width: 720px; border-collapse: collapse;`;
const Th = styled.th`
  padding: 13px 16px; text-align: ${({ align }) => align || "left"}; background: #f8fafc;
  color: #64748b; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
`;
const Td = styled.td`
  padding: 15px 16px; text-align: ${({ align }) => align || "left"}; color: #334155;
  font-size: 14px; border-top: 1px solid #eef2f7;
`;
const Code = styled.span`
  display: inline-block; border: 1px dashed #30d5c8; background: #effcfb; color: #13756e;
  border-radius: 6px; padding: 5px 9px; font-weight: 700; letter-spacing: .04em;
`;
const Amount = styled.span`font-weight: 700; color: #172033;`;
const StatusButton = styled.button`
  display: inline-flex; align-items: center; gap: 7px; min-width: 88px; padding: 7px 10px;
  border: 0; border-radius: 999px; cursor: pointer; font-size: 12px; font-weight: 700;
  color: ${({ $active }) => ($active ? "#087451" : "#a61b1b")};
  background: ${({ $active }) => ($active ? "#dcfce7" : "#fee2e2")};
  &:disabled { cursor: wait; opacity: .65; }
`;
const StatusDot = styled.span`
  width: 7px; height: 7px; flex: 0 0 7px; border-radius: 50%; background: currentColor;
`;
const DeleteButton = styled.button`
  display: inline-flex; align-items: center; gap: 6px; border: 0; border-radius: 7px; padding: 8px 10px;
  color: #b91c1c; background: #fef2f2; cursor: pointer; font-weight: 600;
  &:hover { background: #fee2e2; }
`;
const EmptyState = styled.div`
  min-height: 230px; display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 7px; color: #94a3b8; border-top: 1px solid #eef2f7;
  strong { color: #334155; font-size: 15px; }
  span { font-size: 13px; }
`;
const Loading = styled.div`padding: 70px 20px; text-align: center; color: #64748b;`;
const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 10000; display: grid; place-items: center; padding: 18px;
  background: rgba(15, 23, 42, .55); backdrop-filter: blur(2px);
  overflow-y: auto;
  @media (max-width: 480px) { align-items: end; padding: 0; }
`;
const Modal = styled.div`
  width: min(100%, 460px); max-height: calc(100dvh - 24px); overflow-y: auto; background: #fff; border-radius: 14px; box-shadow: 0 24px 60px rgba(15, 23, 42, .24);
  @media (max-width: 480px) { border-radius: 16px 16px 0 0; }
`;
const ModalHeader = styled.div`display: flex; align-items: flex-start; justify-content: space-between; padding: 22px 22px 16px; border-bottom: 1px solid #eef2f7;`;
const ModalTitle = styled.h2`margin: 0; color: #172033; font-size: 20px;`;
const ModalSubtitle = styled.p`margin: 5px 0 0; color: #64748b; font-size: 13px;`;
const IconButton = styled.button`border: 0; background: transparent; color: #64748b; cursor: pointer; padding: 3px;`;
const Form = styled.form`padding: 22px;`;
const Field = styled.div`margin-bottom: 17px;`;
const Label = styled.label`display: block; margin-bottom: 7px; color: #334155; font-size: 13px; font-weight: 650;`;
const Input = styled.input`
  box-sizing: border-box; width: 100%; border: 1px solid #dbe3ed; border-radius: 8px; padding: 10px 12px;
  color: #172033; outline: none; font-size: 14px;
  &:focus { border-color: #30d5c8; box-shadow: 0 0 0 3px rgba(48, 213, 200, .12); }
`;
const Select = styled.select`
  box-sizing: border-box; width: 100%; border: 1px solid #dbe3ed; border-radius: 8px;
  padding: 10px 12px; color: #172033; background: #fff; outline: none; font-size: 14px;
  &:focus { border-color: #30d5c8; box-shadow: 0 0 0 3px rgba(48, 213, 200, .12); }
`;
const AmountInput = styled.div`
  position: relative;
  span { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #64748b; z-index: 1; }
  input { padding-left: 30px; }
`;
const Actions = styled.div`
  display: flex; justify-content: flex-end; gap: 10px; margin-top: 25px;
  @media (max-width: 480px) { button { flex: 1; } }
`;
const SecondaryButton = styled.button`
  border: 1px solid #dbe3ed; border-radius: 8px; padding: 10px 16px; color: #475569;
  background: #fff; cursor: pointer; font-weight: 600;
  &:hover { background: #f8fafc; }
`;
